import type { Aggregate } from 'edit/Aggregate';
import { Property } from 'edit/Property';
import type { SchedulingWindowReference } from 'edit/reference/EditSchedulingWindowReference';
import type { Recurrence, RecurrenceUnit } from 'api/recurrence_definitions/endpoints/types';
import type { EditableRecurrenceDefinition } from './EditableRecurrenceDefinition';

export type { RecurrenceUnit };

/**
 * The radio choice between an interval rule ("every N units") and a
 * window-open rule. Kept decoupled from `unit`: `mode` is the radio, `unit` is
 * the interval-unit dropdown. The wire collapses both into a single `unit`
 * field (where `window_open` is one of the values); `toSave` resolves the
 * asymmetry. Decoupling lets the user toggle mode without losing the interval
 * unit they previously picked.
 */
export type RecurrenceMode = 'interval' | 'window_open';

/**
 * UI-side type for the interval-mode unit dropdown: every recurrence unit
 * except `window_open` (which `mode` represents instead). Exposing it as a
 * sibling of `RecurrenceUnit` makes the field's intent explicit — `unit` is
 * the chosen interval unit, never `window_open`.
 */
export type IntervalRecurrenceUnit = Exclude<RecurrenceUnit, 'window_open'>;

// Monotonic source of stable per-instance keys for React list rendering. New
// recurrences have no server id, and array index is unstable across add/remove,
// so each instance gets a unique key at construction that never changes.
let nextRecurrenceKey = 0;

const DEFAULT_UNIT: IntervalRecurrenceUnit = 'day';
const DEFAULT_INTERVAL = 1;
const DEFAULT_PRESERVE_TIME = true;
const DEFAULT_SKIP_WHEN_WINDOW_CLOSED = true;

// Wire format for scheduling_window / during_scheduling_window is
// `zone_name|window_name`; the backend validates with `/^[^|]+\|[^|]+$/`.
// Parsing here keeps the editable shape object-typed end-to-end and the
// stringification at the `toSave` boundary.
const parseSchedulingWindow = (s: string | undefined): SchedulingWindowReference | null => {
  if (s === undefined) return null;
  const [zone_name, name] = s.split('|');
  return name !== undefined ? { zone_name, name } : null;
};

const stringifySchedulingWindow = (w: SchedulingWindowReference | null): string | undefined => (w !== null ? `${w.zone_name}|${w.name}` : undefined);

/**
 * One recurrence rule, as a reactive child of an `EditableRecurrenceDefinition`.
 *
 * The parent→child relationship is owned by a shared `Aggregate` that both
 * sides hold: this child routes its back-reference and self-delete through it,
 * so the parent never has to expose array-mutation methods. Every user-fillable
 * field is a `Property`, surfaced as a getter + a bound `set*` alias.
 *
 * There is intentionally no cross-field logic on the setters. Values typed in
 * the non-current `mode` branch are preserved by the disabled-fieldset form
 * control and ignored by the server when the other mode is active, so a mode
 * toggle never has to clear or seed a field — `toSave` reads only what the
 * chosen mode implies.
 */
export class EditableRecurrence {
  private readonly _aggregate: Aggregate<EditableRecurrenceDefinition, EditableRecurrence>;
  private readonly _description = new Property<string | null>(null);
  private readonly _duringSchedulingWindow = new Property<SchedulingWindowReference | null>(null);
  private readonly _interval = new Property<number | null>(DEFAULT_INTERVAL);
  /** Stable identity for React list keys; never changes after construction. */
  readonly key = `recurrence-${(nextRecurrenceKey += 1)}`;
  private readonly _mode = new Property<RecurrenceMode>('interval');
  private readonly _preserveTime = new Property<boolean>(DEFAULT_PRESERVE_TIME);
  private readonly _schedulingWindow = new Property<SchedulingWindowReference | null>(null);
  private readonly _skipWhenWindowClosed = new Property<boolean>(DEFAULT_SKIP_WHEN_WINDOW_CLOSED);
  private readonly _startTime = new Property<string | null>(null);
  private readonly _startTimeZone = new Property<string | null>(null);
  private readonly _unit = new Property<IntervalRecurrenceUnit>(DEFAULT_UNIT);

  /**
   * @param aggregate the shared parent→children owner; the back-reference and
   *   self-delete route through it.
   * @param wire when present, seeds the fields from a loaded recurrence; when
   *   absent, the field defaults stand (a fresh, empty interval rule).
   */
  constructor(aggregate: Aggregate<EditableRecurrenceDefinition, EditableRecurrence>, wire?: Recurrence) {
    this._aggregate = aggregate;
    if (wire !== undefined) {
      this._description.set(wire.description ?? null);
      // A window_open wire has no interval unit to remember — fall back to the
      // default so the dropdown is sane if the user toggles mode to interval.
      this._mode.set(wire.unit === 'window_open' ? 'window_open' : 'interval');
      this._unit.set(wire.unit === 'window_open' ? DEFAULT_UNIT : wire.unit);
      this._interval.set(wire.interval ?? DEFAULT_INTERVAL);
      this._schedulingWindow.set(parseSchedulingWindow(wire.scheduling_window));
      this._duringSchedulingWindow.set(parseSchedulingWindow(wire.during_scheduling_window));
      this._preserveTime.set(wire.preserve_time ?? DEFAULT_PRESERVE_TIME);
      this._skipWhenWindowClosed.set(wire.skip_when_window_closed ?? DEFAULT_SKIP_WHEN_WINDOW_CLOSED);
      this._startTime.set(wire.start_time ?? null);
      this._startTimeZone.set(wire.start_time_zone ?? null);
    }
  }

  get description(): string | null {
    return this._description.get();
  }
  setDescription = this._description.set;

  get duringSchedulingWindow(): SchedulingWindowReference | null {
    return this._duringSchedulingWindow.get();
  }
  setDuringSchedulingWindow = this._duringSchedulingWindow.set;

  /**
   * Human-readable summary of this rule, or `null` when it isn't complete
   * enough to phrase. Presentational helper; the dialog renders it when set.
   */
  get explanation(): string | null {
    if (this.mode === 'window_open') {
      if (this.schedulingWindow === null) return null;
      return `Executes whenever the scheduling window "${this.schedulingWindow.name}" opens.`;
    }
    if (this.interval === null) return null;
    return `Executes every ${this.interval} ${this.unit}(s).`;
  }

  get interval(): number | null {
    return this._interval.get();
  }
  setInterval = this._interval.set;

  get mode(): RecurrenceMode {
    return this._mode.get();
  }
  setMode = this._mode.set;

  get preserveTime(): boolean {
    return this._preserveTime.get();
  }
  setPreserveTime = this._preserveTime.set;

  /** Back-reference to the owning definition, via the shared aggregate. */
  get recurrenceDefinition(): EditableRecurrenceDefinition {
    return this._aggregate.parent;
  }

  get schedulingWindow(): SchedulingWindowReference | null {
    return this._schedulingWindow.get();
  }
  setSchedulingWindow = this._schedulingWindow.set;

  get skipWhenWindowClosed(): boolean {
    return this._skipWhenWindowClosed.get();
  }
  setSkipWhenWindowClosed = this._skipWhenWindowClosed.set;

  get startTime(): string | null {
    return this._startTime.get();
  }
  setStartTime = this._startTime.set;

  get startTimeZone(): string | null {
    return this._startTimeZone.get();
  }
  setStartTimeZone = this._startTimeZone.set;

  get unit(): IntervalRecurrenceUnit {
    return this._unit.get();
  }
  setUnit = this._unit.set;

  /** Remove this recurrence from its definition. */
  delete = (): void => {
    this._aggregate.remove(this);
  };

  /**
   * Project to the wire shape. The only mode/unit asymmetry: a window_open
   * mode emits `unit: 'window_open'`, otherwise the chosen interval unit. All
   * other fields are forwarded as-is and validated by the server; server-
   * assigned `sort_order` / id are deliberately omitted (the definition
   * conveys order by array position).
   */
  toSave(): Recurrence {
    return {
      description: this._description.get() ?? undefined,
      unit: this._mode.get() === 'window_open' ? 'window_open' : this._unit.get(),
      interval: this._interval.get() ?? undefined,
      scheduling_window: stringifySchedulingWindow(this._schedulingWindow.get()),
      during_scheduling_window: stringifySchedulingWindow(this._duringSchedulingWindow.get()),
      preserve_time: this._preserveTime.get(),
      skip_when_window_closed: this._skipWhenWindowClosed.get(),
      start_time: this._startTime.get() ?? undefined,
      start_time_zone: this._startTimeZone.get() ?? undefined,
    };
  }
}
